// Normalizes "closed score" SATB MusicXML (2 staves, 2 voices per staff: SA on top,
// TB on bottom) into the "open score" layout (4 parts, 1 voice each) that the rest of
// the app is built around. Files that don't match the closed-score signature are
// returned completely untouched, so existing 4-part (and any other) files are unaffected.

const CLOSED_SCORE_PART_COUNT = 2;
// Voice 2 must appear in at least this fraction of note-bearing measures for a part to
// count as "two parts sharing a staff" (occasional divisi in a normal part stays below this).
const SECOND_VOICE_PERVASIVENESS_THRESHOLD = 0.5;

const DEFAULT_VOICE_NAMES = [
  ['Soprano', 'Alto'],
  ['Tenor', 'Bass']
];
const DEFAULT_VOICE_ABBREVIATIONS = [
  ['S.', 'A.'],
  ['T.', 'B.']
];

const getDirectChild = (element, tagName) => {
  for (const child of element.children) {
    if (child.tagName === tagName) return child;
  }
  return null;
};

const getDirectChildText = (element, tagName) => {
  const child = getDirectChild(element, tagName);
  return child ? child.textContent : null;
};

const getNoteDuration = (noteEl) => {
  const durationText = getDirectChildText(noteEl, 'duration');
  const duration = parseInt(durationText, 10);
  return Number.isFinite(duration) ? duration : 0; // grace notes have no duration
};

// Analyze one <part>: which voices it uses, whether it is single-staff, and how
// pervasive the second voice is. Returns null if the part can't be a closed-score
// staff at all (multi-staff, or more than two voices).
const analyzePart = (partEl) => {
  const voiceMeasureCounts = new Map(); // voice -> number of measures containing it
  let measuresWithNotes = 0;

  for (const measure of partEl.children) {
    if (measure.tagName !== 'measure') continue;

    const stavesText = measure.querySelector('attributes > staves');
    if (stavesText && parseInt(stavesText.textContent, 10) > 1) {
      return null; // multi-staff part (e.g. piano) — not our format
    }

    const voicesInMeasure = new Set();
    for (const child of measure.children) {
      if (child.tagName !== 'note') continue;

      const staffText = getDirectChildText(child, 'staff');
      if (staffText && parseInt(staffText, 10) > 1) {
        return null; // notes on a second staff — not our format
      }

      const voice = getDirectChildText(child, 'voice');
      if (voice !== null) voicesInMeasure.add(voice.trim());
    }

    if (voicesInMeasure.size > 0) {
      measuresWithNotes++;
      for (const voice of voicesInMeasure) {
        voiceMeasureCounts.set(voice, (voiceMeasureCounts.get(voice) || 0) + 1);
      }
    }
  }

  if (voiceMeasureCounts.size < 1 || voiceMeasureCounts.size > 2 || measuresWithNotes === 0) {
    return null;
  }

  // Sort so the numerically lower voice id is treated as the upper (S/T) line
  const voices = [...voiceMeasureCounts.keys()].sort((a, b) => {
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });

  const secondVoiceShare = voices.length === 2
    ? voiceMeasureCounts.get(voices[1]) / measuresWithNotes
    : 0;

  // A staff shared by two singers can spend long stretches in unison (one voice);
  // derive both parts from the one voice there.
  if (voices.length === 1) voices.push(voices[0]);

  return { voices, secondVoiceShare };
};

// Extract the two display names from a combined part-name like "Soprano\nAlto".
// Returns null when the text doesn't hold exactly two names.
const splitCombinedName = (text) => {
  if (text) {
    const pieces = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (pieces.length === 2) return pieces;
  }
  return null;
};

// Map onset position -> the <lyric> elements of the note starting there. Closed
// scores carry lyrics on one voice only (usually the soprano); this map lets the
// other derived parts pick up the words at matching beats.
const collectLyricsByOnset = (measureEl) => {
  const lyricsByOnset = new Map();
  let cursor = 0;
  for (const child of measureEl.children) {
    const tag = child.tagName;
    if (tag === 'note') {
      const isChord = getDirectChild(child, 'chord') !== null;
      const duration = getNoteDuration(child);
      if (!isChord) {
        const lyrics = [...child.children].filter(c => c.tagName === 'lyric');
        if (lyrics.length > 0 && !lyricsByOnset.has(cursor)) {
          lyricsByOnset.set(cursor, lyrics);
        }
        cursor += duration;
      }
    } else if (tag === 'backup') {
      cursor -= getNoteDuration(child);
    } else if (tag === 'forward') {
      cursor += getNoteDuration(child);
    }
  }
  return lyricsByOnset;
};

// Build a copy of `measureEl` containing only the notes of `voice`. Other voices'
// notes are dropped; their time is preserved by tracking the measure cursor and
// inserting <forward> gaps where needed. Non-note elements (attributes, directions,
// barlines, harmony, prints, ...) are copied through so both derived parts keep
// keys, clefs, tempos and repeats. Notes without their own lyrics receive copies of
// the `lyricsByOnset` words starting on the same beat.
const buildSingleVoiceMeasure = (doc, measureEl, voice, lyricsByOnset) => {
  // Unison passages: when this measure has notes but none in the requested voice
  // (e.g. a verse written as one shared melody line), both singers perform the
  // voice that IS there — derive from it instead of padding with rests.
  const voicesInMeasure = new Set();
  for (const child of measureEl.children) {
    if (child.tagName !== 'note') continue;
    const v = getDirectChildText(child, 'voice');
    if (v !== null) voicesInMeasure.add(v.trim());
  }
  if (!voicesInMeasure.has(voice) && voicesInMeasure.size === 1) {
    voice = [...voicesInMeasure][0];
  }

  const newMeasure = measureEl.cloneNode(false);
  let cursor = 0;    // position while walking the original measure
  let outPos = 0;    // position filled so far in the rebuilt measure
  let maxPos = 0;    // measure length as actually filled by any voice
  let appendedNote = false;

  const appendForwardGap = (gap) => {
    const forward = doc.createElement('forward');
    const duration = doc.createElement('duration');
    duration.textContent = String(gap);
    forward.appendChild(duration);
    newMeasure.appendChild(forward);
  };

  for (const child of measureEl.children) {
    const tag = child.tagName;

    if (tag === 'note') {
      const isChord = getDirectChild(child, 'chord') !== null;
      const duration = getNoteDuration(child);
      const noteVoice = (getDirectChildText(child, 'voice') || '').trim();

      if (noteVoice === voice) {
        if (!isChord && cursor > outPos) {
          appendForwardGap(cursor - outPos);
          outPos = cursor;
        }
        const clone = child.cloneNode(true);
        // Closed scores force stems (up for voice 1, down for voice 2); drop that so
        // the standalone part gets natural stem directions.
        const stem = getDirectChild(clone, 'stem');
        if (stem) clone.removeChild(stem);
        // Share the staff's lyrics with every derived part: a singable note that has
        // no words of its own gets the lyrics starting on the same beat.
        const isRest = getDirectChild(clone, 'rest') !== null;
        const isGrace = getDirectChild(clone, 'grace') !== null;
        if (!isChord && !isRest && !isGrace && !getDirectChild(clone, 'lyric')) {
          const lyrics = lyricsByOnset && lyricsByOnset.get(cursor);
          if (lyrics) {
            for (const lyric of lyrics) clone.appendChild(lyric.cloneNode(true));
          }
        }
        newMeasure.appendChild(clone);
        appendedNote = true;
        if (!isChord) outPos += duration;
      }
      if (!isChord) cursor += duration;
      if (cursor > maxPos) maxPos = cursor;
    } else if (tag === 'backup') {
      cursor -= getNoteDuration(child);
    } else if (tag === 'forward') {
      cursor += getNoteDuration(child);
      if (cursor > maxPos) maxPos = cursor;
    } else {
      newMeasure.appendChild(child.cloneNode(true));
    }
  }

  // A measure where this voice is absent entirely: pad with a whole-measure rest so
  // the part stays in sync.
  if (!appendedNote && maxPos > 0) {
    const note = doc.createElement('note');
    const rest = doc.createElement('rest');
    rest.setAttribute('measure', 'yes');
    const duration = doc.createElement('duration');
    duration.textContent = String(maxPos);
    const voiceEl = doc.createElement('voice');
    voiceEl.textContent = voice;
    note.appendChild(rest);
    note.appendChild(duration);
    note.appendChild(voiceEl);
    newMeasure.appendChild(note);
  }

  return newMeasure;
};

const buildScorePart = (doc, newId, name, abbreviation) => {
  const scorePart = doc.createElement('score-part');
  scorePart.setAttribute('id', newId);
  const partName = doc.createElement('part-name');
  partName.textContent = name;
  scorePart.appendChild(partName);
  const partAbbrev = doc.createElement('part-abbreviation');
  partAbbrev.textContent = abbreviation;
  scorePart.appendChild(partAbbrev);
  return scorePart;
};

/**
 * If `xmlContent` is a closed-score SATB file (2 parts, 2 voices per staff),
 * returns `{ content, wasNormalized: true }` where `content` is the equivalent
 * 4-part open score. In every other case returns the input untouched with
 * `wasNormalized: false`.
 */
export const normalizeClosedScoreSATB = (xmlContent) => {
  const passthrough = { content: xmlContent, wasNormalized: false };

  try {
    if (typeof xmlContent !== 'string' || !xmlContent.includes('<score-partwise')) {
      return passthrough;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');
    if (doc.querySelector('parsererror')) return passthrough;

    const partList = doc.querySelector('score-partwise > part-list');
    const scoreParts = partList ? [...partList.children].filter(el => el.tagName === 'score-part') : [];
    const parts = [...doc.querySelectorAll('score-partwise > part')];

    if (scoreParts.length !== CLOSED_SCORE_PART_COUNT || parts.length !== CLOSED_SCORE_PART_COUNT) {
      return passthrough;
    }

    // Every part must be a plausible closed-score staff (single staff, <= 2 voices)
    const analyses = parts.map(analyzePart);
    if (analyses.some(a => a === null)) return passthrough;

    // Two accepted signatures:
    // 1. Every part-name carries two names ("Soprano\nAlto") — the arranger's own
    //    statement that each staff holds two singers. Unison-heavy files qualify.
    // 2. No such names, but every part keeps two voices in most measures — the
    //    conservative structural signature. Occasional divisi stays below the
    //    threshold, so normal two-singer duets are never split.
    const namesIndicateTwoSingers = scoreParts.every(
      sp => splitCombinedName(getDirectChildText(sp, 'part-name')) !== null
    );
    const structureIndicatesTwoSingers = analyses.every(
      a => a.secondVoiceShare >= SECOND_VOICE_PERVASIVENESS_THRESHOLD
    );
    if (!namesIndicateTwoSingers && !structureIndicatesTwoSingers) return passthrough;

    // Lyrics per part, measure by measure (onset -> lyric elements). A staff with no
    // lyrics at all (typically tenor/bass, singing the same words) borrows the other
    // staff's, so every derived part ends up with the text under its own staff.
    const partLyricMaps = parts.map(partEl =>
      [...partEl.children].filter(c => c.tagName === 'measure').map(collectLyricsByOnset)
    );
    partLyricMaps.forEach((maps, i) => {
      if (maps.every(m => m.size === 0)) {
        const donor = partLyricMaps.find(other => other !== maps && other.some(m => m.size > 0));
        if (donor) partLyricMaps[i] = donor;
      }
    });

    scoreParts.forEach((scorePartEl, partIndex) => {
      const partId = scorePartEl.getAttribute('id');
      const partEl = parts.find(p => p.getAttribute('id') === partId);
      if (!partEl) throw new Error(`part-list references missing part ${partId}`);

      const { voices } = analyses[parts.indexOf(partEl)];
      const names = splitCombinedName(getDirectChildText(scorePartEl, 'part-name'))
        || DEFAULT_VOICE_NAMES[partIndex] || DEFAULT_VOICE_NAMES[0];
      const abbreviations = splitCombinedName(getDirectChildText(scorePartEl, 'part-abbreviation'))
        || DEFAULT_VOICE_ABBREVIATIONS[partIndex] || DEFAULT_VOICE_ABBREVIATIONS[0];

      voices.forEach((voice, voiceIndex) => {
        const newId = `${partId}-V${voiceIndex + 1}`;

        partList.insertBefore(
          buildScorePart(doc, newId, names[voiceIndex], abbreviations[voiceIndex]),
          scorePartEl
        );

        const lyricMaps = partLyricMaps[parts.indexOf(partEl)];
        const newPart = doc.createElement('part');
        newPart.setAttribute('id', newId);
        let measureIndex = 0;
        for (const measure of partEl.children) {
          if (measure.tagName !== 'measure') continue;
          newPart.appendChild(buildSingleVoiceMeasure(doc, measure, voice, lyricMaps[measureIndex]));
          measureIndex++;
        }
        partEl.parentNode.insertBefore(newPart, partEl);
      });

      partList.removeChild(scorePartEl);
      partEl.parentNode.removeChild(partEl);
    });

    const serialized = new XMLSerializer().serializeToString(doc);
    console.log('🎼 Closed-score SATB detected (2 voices per staff) — normalized to 4 separate parts');
    return { content: serialized, wasNormalized: true };
  } catch (error) {
    console.warn('SATB normalization skipped due to error, using original file:', error.message);
    return passthrough;
  }
};
